import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
	type Ref,
	useMemo,
	type MouseEvent,
	type KeyboardEvent,
} from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { ColorPaletteMenu, Compact } from '@atlaskit/color-picker';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import EditorMoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--editor-more';
import { token } from '@atlaskit/tokens';
import {
	COLOR_CHECKMARK_MAP,
	COLOR_MAP,
	COLOR_NAME_MAP,
} from '@atlassian/jira-dashboard-common/src/constants.tsx';
import type { GadgetColor } from '@atlassian/jira-dashboard-common/src/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useSwitchModals } from '@atlassian/jira-switch-modals-controller/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { DeleteGadgetModal } from './delete-gadget-modal/index.tsx';
import messages from './messages.tsx';
import type { ConfigDropdownProps } from './types.tsx';

export const ConfigDropdown = ({
	isEditable,
	isEditDisabled,
	isDeprecatedGadget,
	gadgetColor,
	gadgetId,
	gadgetTitle,
	onSaveColor,
	onEditModeClick,
	onDeleteGadget,
	onDuplicateGadget,
}: ConfigDropdownProps) => {
	const { formatMessage } = useIntl();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [shouldFocusModalTrigger, setShouldFocusModalTrigger] = useState(false);
	const [, { on: gadgetDeleteOpen }] = useSwitchModals(gadgetId);
	const modalTriggerRef = useRef<HTMLElement>(null);
	const isMounted = useRef(true);
	const colorPalette = useMemo(
		() =>
			Object.values(COLOR_MAP).map((code) => {
				const colorName = COLOR_NAME_MAP[code];
				return {
					label: colorName ? formatMessage(colorName) : code,
					value: code,
				};
			}),
		[formatMessage],
	);

	const handleDropdownOpenChange = useCallback(
		({ isOpen }: { isOpen: boolean }) => setIsDropdownOpen(isOpen),
		[],
	);

	const handleModalClose = useCallback(() => {
		setTimeout(() => {
			// prevent unwanted state update if component is unmounted
			if (isMounted.current) {
				// Added 1ms delay to allow for the modal onClose event to finish
				// This is because closing the modal via the `ESC` key will trigger
				// the dropdown menu to close simultaneously, so we must dispatch this
				// after the keypress event has finished
				setIsDropdownOpen(true);
				setShouldFocusModalTrigger(true);
			}
		}, 1);
	}, []);

	useEffect(() => {
		if (shouldFocusModalTrigger && isDropdownOpen) {
			// DropdownMenu component uses useFocusManager hook to focus the first
			// dropdown menu item when the menu is opened, so we need to wait roughly
			// 50ms for the menu to open and focus the first item before then focusing
			// the modal trigger.
			setTimeout(() => {
				modalTriggerRef.current?.focus();
			}, 50);
			setShouldFocusModalTrigger(false);
		}
	}, [isDropdownOpen, shouldFocusModalTrigger]);

	useEffect(
		() => () => {
			isMounted.current = false;
		},
		[],
	);

	const onColourChange = useCallback(
		(_: MouseEvent | KeyboardEvent, newColor: string) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const colorId = (Object.keys(COLOR_MAP) as GadgetColor[]).find(
				(key) => COLOR_MAP[key] === newColor,
			);

			if (colorId) {
				onSaveColor(colorId);
			}
		},
		[onSaveColor],
	);

	return (
		<>
			<DropdownWrapper>
				<DropdownMenu
					isOpen={isDropdownOpen}
					onOpenChange={handleDropdownOpenChange}
					isLoading={false}
					trigger={({ triggerRef, ...triggerProps }: { triggerRef: Ref<HTMLElement> }) => (
						<Button
							{...triggerProps}
							appearance="subtle"
							spacing="default"
							iconBefore={
								<EditorMoreIcon
									label={formatMessage(messages.moreActions, { gadgetTitle })}
									LEGACY_size="medium"
									spacing="spacious"
								/>
							}
							testId="dashboard-edit.ui.gadget-editable.config-dropdown.dropdown-button"
							ref={triggerRef}
						/>
					)}
					shouldFlip
					placement="bottom-end"
					appearance="tall"
					shouldRenderToParent
				>
					<DropdownItemGroup title={formatMessage(messages.colorHeading)}>
						<ColorWrapper>
							<ColorPaletteMenu
								label={formatMessage(messages.colorHeading)}
								palette={colorPalette}
								selectedColor={COLOR_MAP[gadgetColor]}
								checkMarkColor={COLOR_CHECKMARK_MAP[gadgetColor]}
								mode={Compact}
								cols={Object.keys(COLOR_MAP).length}
								onChange={onColourChange}
							/>
						</ColorWrapper>
					</DropdownItemGroup>
					<DropdownItemGroup>
						{isEditable && (
							<DropdownItem
								data-testid="dashboard-edit.ui.gadget-editable.config-dropdown.edit"
								isDisabled={isEditDisabled}
								onClick={onEditModeClick}
							>
								{formatMessage(messages.configure)}
							</DropdownItem>
						)}
						{!(isDeprecatedGadget && fg('endeavour_gadget_deprecation_remove_functionality')) && (
							<DropdownItem
								data-testid="dashboard-edit.ui.gadget-editable.config-dropdown.duplicate"
								onClick={onDuplicateGadget}
							>
								{formatMessage(messages.duplicate)}
							</DropdownItem>
						)}
						<DropdownItem
							data-testid="dashboard-edit.ui.gadget-editable.config-dropdown.delete"
							ref={modalTriggerRef}
							onClick={gadgetDeleteOpen}
						>
							{formatMessage(messages.delete)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			</DropdownWrapper>
			<DeleteGadgetModal
				id={gadgetId}
				onDeleteGadget={onDeleteGadget}
				gadgetName={gadgetTitle}
				onClose={handleModalClose}
			/>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownWrapper = styled.div({
	display: 'inline-block',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorWrapper = styled.div({
	paddingTop: 0,
	paddingRight: token('space.075'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `solid 1px ${token('color.border')}`,
});
